import React from 'react';
import styled from 'styled-components';
import Reveal from '../components/Reveal';
import { REVEAL_TRANSITION } from '../animations';
import { services } from '../content';

const Section = styled.section`
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.container.gutter};
`;

const Inner = styled.div`
  max-width: ${({ theme }) => theme.container.maxWidth};
  margin: 0 auto;
`;

const Header = styled(Reveal)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: clamp(40px, 6vw, 72px);
`;

const Kicker = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.76rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 18px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4.6vw, 3.9rem);
  line-height: 1;
  letter-spacing: -0.025em;
  font-weight: 700;
  max-width: 18ch;
`;

const Intro = styled.p`
  max-width: 34ch;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1.05rem;
  line-height: 1.5;
`;

/*
 * The 1px gap over a line-coloured background draws the hairline rules between cards.
 * Column counts are fixed rather than auto-fit: any count that doesn't divide the six
 * services leaves empty tracks, which read as grey slabs against that backing.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  background: ${({ theme }) => theme.colors.line};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 22px;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Reveal)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 280px;
  padding: clamp(28px, 3vw, 40px);
  background: ${({ theme }) => theme.colors.paper};
  transition: background-color 0.3s ease, ${REVEAL_TRANSITION};

  &:hover {
    background: ${({ theme }) => theme.colors.white};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 0;
  }
`;

const Num = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const CardTitle = styled.h3`
  font-size: 1.44rem;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const CardText = styled.p`
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1rem;
  line-height: 1.55;
`;

const Services: React.FC = () => (
  <Section id="services">
    <Inner>
      <Header>
        <div>
          <Kicker>01 / Our services</Kicker>
          <Title>
            Robust, scalable solutions that deliver real <em>value</em>.
          </Title>
        </div>
        <Intro>
          Comprehensive technology consulting and development — the full span from architecture to
          delivery.
        </Intro>
      </Header>

      <Grid>
        {services.map(({ num, title, desc, delay }) => (
          <Card key={num} delay={delay}>
            <Num>{num}</Num>
            <CardTitle>{title}</CardTitle>
            <CardText>{desc}</CardText>
          </Card>
        ))}
      </Grid>
    </Inner>
  </Section>
);

export default Services;
